import React, { useState, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaFacebookF, FaTwitter, FaWhatsapp, FaTimes, FaShareAlt, FaCheck, FaCopy, FaLink } from 'react-icons/fa';
import DarkModeContext from './DarkModeContext';

const ShareProfileModal = ({ isOpen, onClose, nickname }) => {
  const { darkMode } = useContext(DarkModeContext);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !nickname) return null;

  const profileUrl = `${window.location.origin}/${nickname}`;
  const shareText = `Mira el perfil de ${nickname} en YourPack 🔥`;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: nickname, text: shareText, url: profileUrl }).catch(() => {});
    }
  };

  const socials = [
    {
      label: 'Facebook',
      icon: <FaFacebookF className="text-base" />,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}&quote=${encodeURIComponent(shareText)}`,
    },
    {
      label: 'Twitter / X',
      icon: <FaTwitter className="text-base" />,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareText} ${profileUrl}`)}`,
    },
    {
      label: 'WhatsApp',
      icon: <FaWhatsapp className="text-base" />,
      color: '#25D366',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} ${profileUrl}`)}`,
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className={`relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="bg-white/20 rounded-full p-1.5">
              <FaShareAlt className="text-white text-sm" />
            </div>
            <div>
              <p className="text-white font-semibold text-base leading-tight">Compartir perfil</p>
              <p className="text-white/75 text-xs" translate="no">@{nickname}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            aria-label="Cerrar"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-4">
          {/* Social buttons */}
          <div className="flex flex-col gap-2.5">
            {socials.map(({ label, icon, color, url }) => (
              <button
                key={label}
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                className="text-white flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 w-full font-medium text-sm shadow-sm hover:shadow-sm hover:-translate-y-0.5 hover:opacity-90"
                style={{ backgroundColor: color }}
              >
                <span className="w-5 flex justify-center">{icon}</span>
                <span>{label}</span>
              </button>
            ))}

            {/* Native share — solo si el navegador lo soporta */}
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                onClick={handleNativeShare}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 w-full font-medium text-sm border ${
                  darkMode
                    ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="w-5 flex justify-center"><FaShareAlt className="text-base" /></span>
                <span>Más opciones</span>
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className={`flex-1 h-px ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>o copia el enlace</span>
            <div className={`flex-1 h-px ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>

          {/* Copy URL */}
          <div className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <FaLink className={`flex-shrink-0 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <span className={`flex-1 min-w-0 text-xs truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {profileUrl}
            </span>
            <button
              onClick={handleCopyUrl}
              className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-rose-500 hover:bg-rose-600 text-white'
              }`}
            >
              {copied
                ? <><FaCheck className="text-xs" /> Copiado</>
                : <><FaCopy className="text-xs" /> Copiar</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareProfileModal;
